import { List as IList } from 'immutable';
import { getActivePromoCode, getPromoNudgesValue } from '../promo/promoSelectors';
import { getAllActiveCards, getItemsAccessoryCount } from '../productCartHandler/productCartSelectors';
import { getLoyaltyNumber } from '../loyalty/loyaltySelectors';
import { getBrandCode, getEgcRecommendedDenoms, getPlasticRecommendedDenoms } from '../brand/brandSelectors';
import alphaNumericNormalizer from '../addressFields/validate/alphaNumericNormalizer';
import { genericCartSegmentData } from './segmentSelectors';
import {
    isClickedOnUYO,
    getDigitalFaceplateGroups,
    getPhysicalFaceplateGroups
} from '../landingPage/landingPageSelectors';

export const collectValues = (items, key, separator = ', ') => {
    if (!Array.isArray(items) || items.length === 0) return '';
    return items
        .map(item => item[key])
        .filter(fieldValue => fieldValue !== undefined && fieldValue !== null && fieldValue !== '')
        .join(separator);
};

/**
 * Resolves the standardized gift card type from a single product item.
 * Products always come from getProductsSegment (segmentSelectors.js), which
 * returns plain objects with a `card_type` of 'Plastic' | 'Print' | 'Digital'.
 * @param {Object} item - The product item.
 * @returns {string} The standardized card type string.
 */
const resolveSingleCardType = (item) => {
    const rawType = (item && item.card_type) || '';
    if (rawType === 'Plastic') return 'Physical';
    if (rawType === 'Print') return 'PrintAtHome';
    return rawType;
};

const checkPromoNudgesPresent = (state) => {
    const promoNudges = state && getPromoNudgesValue(state);
    return promoNudges && promoNudges.nudges && promoNudges.nudges.length > 0 ? 'YES' : 'NO';
};

const buildHomePageTraits = (data, state) => {
    const traits = {};
    traits['home-page_uyo'] = isClickedOnUYO(state) ? 'YES' : 'NO';

    const properties = data.properties || {};
    if (properties.buyFlow !== undefined) {
        traits['home-page_buy_flow'] = properties.buyFlow;
    }
    traits['home-page_type'] = properties.isGridViewHomePage ? 'Grid-view' : 'Carousal-view';

    return traits;
};

const normalizeCategory = value => (value ? alphaNumericNormalizer(value).toLowerCase() : '');

/**
 * Resolves the default (first, by ordinal) category for a card type.
 * @param {Immutable.Map} state - The Redux root state.
 * @param {boolean} isPhysical - Whether to read the physical groups instead of the digital ones.
 * @returns {string} The normalized default category, or '' if unresolvable.
 */
const getDefaultCategory = (state, isPhysical) => {
    const groups = isPhysical ? getPhysicalFaceplateGroups(state) : getDigitalFaceplateGroups(state);
    const defaultGroup = groups && groups.first();
    return defaultGroup ? normalizeCategory(defaultGroup.get('name')) : '';
};

/**
 * Resolves the default (first) faceplate code for a category within a card type's groups.
 * @param {Immutable.Map} state - The Redux root state.
 * @param {boolean} isPhysical - Whether to read the physical groups instead of the digital ones.
 * @param {string} category - The normalized category to match.
 * @returns {string} The default faceplate code, or '' if unresolvable.
 */
const getDefaultFaceplateCode = (state, isPhysical, category) => {
    const groups = isPhysical ? getPhysicalFaceplateGroups(state) : getDigitalFaceplateGroups(state);
    let code = '';
    (groups || IList()).some((group) => {
        if (normalizeCategory(group.get('name')) !== category) return false;
        const first = group.get('faceplates') && group.get('faceplates').first();
        code = (first && first.get('code')) || '';
        return true;
    });
    return code;
};

/**
 * Determines, per card in the cart, whether that card's faceplate differs from the
 * default (first) faceplate of its own category. There's no session history of the
 * user's previous pick, so "changed" is relative to the category default.
 * @param {Immutable.List} activeCards - Every active card in the cart.
 * @param {Immutable.Map} state - The Redux root state.
 * @returns {string} Comma-separated YES/NO per card, or '' if unresolvable.
 */
const resolveFaceplateChanged = (activeCards, state) => {
    if (!state || !activeCards) return '';

    const perCard = activeCards.toJS().map((item) => {
        const faceplate = item.faceplate || {};
        const category = normalizeCategory(faceplate.category);
        if (!category || !faceplate.code) return {};

        const defaultCode = getDefaultFaceplateCode(state, item.itemCardType === 'Physical', category);
        if (!defaultCode) return {};
        return { changed: faceplate.code !== defaultCode ? 'YES' : 'NO' };
    });

    return collectValues(perCard, 'changed');
};

const resolveCategoryChanged = (activeCards, state) => {
    if (!state || !activeCards) return '';

    const defaults = {
        physical: getDefaultCategory(state, true),
        digital: getDefaultCategory(state, false)
    };
    if (!defaults.physical && !defaults.digital) return '';

    const perCard = activeCards.toJS().map((item) => {
        const category = normalizeCategory(item.faceplate && item.faceplate.category);
        const defaultCategory = item.itemCardType === 'Physical' ? defaults.physical : defaults.digital;
        if (!category || !defaultCategory) return {};
        return { changed: category !== defaultCategory ? 'YES' : 'NO' };
    });

    return collectValues(perCard, 'changed');
};

const buildProductPageTraits = (data, state, action) => {
    const traits = {};
    const activeCards = getAllActiveCards(state);
    const { products } = genericCartSegmentData(activeCards, {
        brandCode: getBrandCode(state),
        promoCode: getActivePromoCode(state),
        egcRecommendedDenoms: getEgcRecommendedDenoms(state),
        plasticRecommendedDenoms: getPlasticRecommendedDenoms(state),
        shippingDisplayTexts: {}
    });

    const faceplateChanged = resolveFaceplateChanged(activeCards, state);
    if (faceplateChanged) {
        traits['product-page_faceplate_changed'] = faceplateChanged;
    }

    const categoryChanged = resolveCategoryChanged(activeCards, state);
    if (categoryChanged) {
        traits['product-page_category_changed'] = categoryChanged;
    }

    const cardTypes = products.map(product => resolveSingleCardType(product)).filter(Boolean);
    if (cardTypes.length > 0) {
        traits['product-page_gift_card_type'] = cardTypes.join(', ');
    }

    const uyoValues = collectValues(products, 'UYO');
    if (uyoValues) {
        traits['product-page_uyo'] = uyoValues;
    }

    const customAmounts = collectValues(products, 'custom_amount');
    if (customAmounts) {
        traits['product-page_custom_amount'] = customAmounts;
    }

    const deliveryTypes = collectValues(products, 'delivery_type');
    if (deliveryTypes) {
        traits['product-page_delivery_method'] = deliveryTypes;
    }

    const purchaseTypes = collectValues(products, 'purchase_type');
    if (purchaseTypes) {
        traits['product-page_purchase_type'] = purchaseTypes;
    }

    if (products.length > 0) {
        traits['product-page_quantity'] = products.length;
    }

    const tellRecipients = collectValues(products, 'tell_recipient');
    if (tellRecipients) {
        traits['product-page_tell_recipient'] = tellRecipients;
    }

    const messages = collectValues(products, 'message');
    if (messages) {
        traits['product-page_message_present'] = messages;
    }

    if (data.currency !== undefined) {
        traits['product-page_currency'] = data.currency;
    }

    
    
    const cartItems = activeCards.toJS();
    const shippingCountries = cartItems
        .filter(item => item && item.itemCardType === 'Physical' && item.shippingAddress
            && item.shippingAddress.countryCode)
        .map(item => item.shippingAddress.countryCode)
        .join(', ');
    if (shippingCountries) {
        traits['product-page_shipping_country'] = shippingCountries;
    }

    
    
    traits['product-page_buy_now_clicked'] = action === 'Buy Now' ? 'YES' : 'NO';
    traits['product-page_add_to_cart_clicked'] = action === 'Add To Cart' ? 'YES' : 'NO';

    return traits;
};

const buildCartPageTraits = (state) => {
    const promoCode = getActivePromoCode(state);
    return {
        'cart-page_promo_applied': promoCode || 'NO',
        'cart-page_promo_nudge_present': checkPromoNudgesPresent(state),
        'cart-page_accessory_present': getItemsAccessoryCount(state) > 0 ? 'YES' : 'NO'
    };
};

const buildPaymentPageTraits = (data, state) => {
    const traits = {};
    if (data.payment_method !== undefined) {
        traits['payment-page_payment_method'] = data.payment_method;
    }
    traits['payment-page_rewards_loyalty_present'] = getLoyaltyNumber(state) ? 'YES' : 'NO';
    return traits;
};

const buildConfirmationPageTraits = data => (
    data.order_id !== undefined ? { 'confirmation-page_order_id': data.order_id } : {}
);

const HOME_PAGE_ACTIONS = new Set(['Buy Gift Card', 'Product Clicked']);
const PRODUCT_PAGE_ACTIONS = new Set(['Buy Now', 'Add To Cart', 'Product Removed', 'Undo Product Removed']);
const CART_PAGE_ACTIONS = new Set(['Promo Applied']);
const PAYMENT_PAGE_ACTIONS = new Set(['Starcode Applied', 'Order Submitted', 'Order Completed']);
const CONFIRMATION_PAGE_ACTIONS = new Set(['Order Submitted', 'Order Completed']);

export const buildHotjarTraits = (data = {}, state, action = '') => {
    let traits = {};

    if (HOME_PAGE_ACTIONS.has(action)) {
        traits = { ...traits, ...buildHomePageTraits(data, state) };
    }
    if (PRODUCT_PAGE_ACTIONS.has(action)) {
        traits = { ...traits, ...buildProductPageTraits(data, state, action) };
    }
    if (CART_PAGE_ACTIONS.has(action)) {
        traits = { ...traits, ...buildCartPageTraits(state) };
    }
    if (PAYMENT_PAGE_ACTIONS.has(action)) {
        traits = { ...traits, ...buildPaymentPageTraits(data, state) };
    }
    if (CONFIRMATION_PAGE_ACTIONS.has(action)) {
        traits = { ...traits, ...buildConfirmationPageTraits(data) };
    }

    return traits;
};
